import Button from "@/common/elements/Button";
import TextField from "@/common/elements/InputElements/TextField";
import { StyledFlex, StyledGridCol, StyledGridRow } from "@/common/styles";
import AuditLogEntriesModal from "./AuditLogEntriesModal";
import { useEffect, useState } from "react";
import { BookingsSectionTitle } from "./styles";
import CommonDatePicker from "@/common/elements/DatePicker";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { Form } from "antd";
import { getRefListNameByID, isNonEmptyVariable } from "@/util/common_function";
import API_ENDPOINTS from "@/util/api";
import api from "@/services/axios_custom";

const BookingAdministrativeInformation = ({
  showActionButton = true,
  showUser = true,
  showHeaderLabel = false,
  headerLabel = "",
  parentKey = "bookings",
  facilityLogDetail = null,
  startingPoint,
}) => {
  const ss = JSON.parse(sessionStorage.getItem("createNewBooking"));
  const [auditLogModal, setAuditLogModal] = useState(false);
  const [auditLogData, setAuditLogData] = useState([]);
  const [form] = Form.useForm();
  const stafflist = useSelector((state) => state?.staff?.activeList);

  const { bookings } = useSelector((s) => s?.bookings);
  const fullSearch = useSelector((s) => s.fullSearch);

  // -----------------------------------------------------------------------
  // Sync audit log data from bookings
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (parentKey !== "facilityLog") {
      getAuditLogData();
    } else {
      setAuditLogData(facilityLogDetail?.auditLogEntries || []);
    }
  }, [facilityLogDetail, bookings]);

  const getAuditLogData = () => {
    const url =
      API_ENDPOINTS.auditLogSearch +
      `?page=0&size=500&top=500&direction=DESC&orderBy=systemAuditLogID`;

    const payload = [
      {
        filterKey: "booking",
        value:
          bookings?.bookingNumber ||
          bookings?.visitorLogId ||
          bookings?.staffId,
      },
      {
        filterKey: "searchType",
        value: "Starts With",
      },
    ];

    api.post(url, payload).then((response) => {
      if (response?.data?.rows?.length > 0) {
        setAuditLogData(response.data.rows);
      } else {
        setAuditLogData([]);
      }
    });
  };

  const getStaffDescription = (staffList, staffId) => {
    const staff = staffList.find((item) => item.staffId === staffId);
    return staff ? staff.staffDescriptionCached : null;
  };
  // -----------------------------------------------------------------------
  // Form data — derived from parentKey
  // -----------------------------------------------------------------------
  const getTime = (time) => {
    if (!time) return null;
    const parts = time.split(" ");
    return parts.length > 1 ? parts[parts.length - 1] : time;
  };

  const formData =
    parentKey !== "facilityLog"
      ? {
          BookingID:
            bookings?.bookingNumber ||
            bookings?.visitorLogId ||
            bookings?.staffId,
          EnteredDate:
            bookings?.dateAdded && bookings?.timeAdded
              ? dayjs(`${bookings.dateAdded} ${getTime(bookings.timeAdded)}`)
              : null,
          userID:
            getStaffDescription(stafflist, bookings?.creatorStaffId) ??
            bookings?.user,
        }
      : {
          BookingID: facilityLogDetail?.commentLogID,
          EnteredDate:
            facilityLogDetail?.dateAdded && facilityLogDetail?.timeAdded
              ? dayjs(
                  `${facilityLogDetail.dateAdded} ${facilityLogDetail.timeAdded}`,
                )
              : null,
          userID: getRefListNameByID(
            "",
            facilityLogDetail?.creatorStaffID,
            true,
            false,
          ),
        };

  const handleAuditLogModal = () => setAuditLogModal((v) => !v);

  const showAuditLogButton =
    (showActionButton &&
      isNonEmptyVariable(ss) &&
      !ss &&
      parentKey !== "staff") ||
    parentKey === "facilityLog";

  // -----------------------------------------------------------------------
  // Render
  //
  // All fields here are audit/admin display fields — always read-only.
  // bookingsFieldsEnabled={fullSearch} mirrors the original Form disabled={!fullSearch}.
  // overrideDisabled on DatePicker mirrors the original disabled={!fullSearch}.
  // -----------------------------------------------------------------------
  return (
    <>
      {showHeaderLabel && (
        <BookingsSectionTitle>
          <span>{headerLabel || "Administrative Information"}</span>
        </BookingsSectionTitle>
      )}

      <FieldSecurityProvider
        entity="Bookings"
        bookingsFieldsEnabled={fullSearch}
        startingPoint={startingPoint}
      >
        <Form form={form} initialValues={formData} disabled={!fullSearch}>
          <StyledGridRow gutter={16}>
            {/* ID */}
            <StyledGridCol span={4}>
              <SecureFormItem
                fieldName="BookingID"
                formItemProps={{ name: "BookingID", label: "ID" }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>

            {/* Date & Time Entered */}
            <StyledGridCol span={6}>
              <SecureFormItem
                fieldName="EnteredDate"
                overrideDisabled={!fullSearch}
                formItemProps={{
                  name: "EnteredDate",
                  label: "Date & Time Entered",
                }}
              >
                <CommonDatePicker
                  format="MM/DD/YYYY HH:mm"
                  style={{
                    backgroundColor: !fullSearch ? "#f5f5f5" : "",
                    color: !fullSearch ? "#a0a0a0" : "",
                  }}
                />
              </SecureFormItem>
            </StyledGridCol>

            {/* User */}
            {showUser && (
              <StyledGridCol span={6}>
                <SecureFormItem
                  fieldName="userID"
                  formItemProps={{ name: "userID", label: "User" }}
                >
                  <TextField />
                </SecureFormItem>
              </StyledGridCol>
            )}

            {/* Audit Log button — not a form field, no security wrapper needed */}
            {!showAuditLogButton && (
              <StyledGridCol>
                <StyledFlex
                  justify="end"
                  align="center"
                  style={{ paddingTop: "28px" }}
                >
                  <Button onClick={handleAuditLogModal}>
                    Audit Log
                  </Button>
                </StyledFlex>
              </StyledGridCol>
            )}
          </StyledGridRow>
        </Form>
      </FieldSecurityProvider>

      <AuditLogEntriesModal
        auditLogData={auditLogData}
        show={auditLogModal}
        onHide={handleAuditLogModal}
      />
    </>
  );
};

export default BookingAdministrativeInformation;
